/**
 * TRUCKER CHICK — THEME JAVASCRIPT
 * Pure vanilla JS — no external libraries
 * Handles: cursor, scroll reveal, header shrink,
 *          mobile drawer, cart ATC, search toggle
 */

(function () {
  'use strict';

  /* ============================================================
     UTILITIES
     ============================================================ */

  /**
   * Throttle a function call
   * @param {Function} fn
   * @param {number} ms
   */
  function throttle(fn, ms) {
    let last = 0;
    return function (...args) {
      const now = Date.now();
      if (now - last >= ms) {
        last = now;
        fn.apply(this, args);
      }
    };
  }

  /**
   * Debounce a function call
   * @param {Function} fn
   * @param {number} ms
   */
  function debounce(fn, ms) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  /**
   * Select single element
   * @param {string} sel
   * @param {Element} ctx
   */
  function $(sel, ctx = document) {
    return ctx.querySelector(sel);
  }

  /**
   * Select all elements
   * @param {string} sel
   * @param {Element} ctx
   */
  function $$(sel, ctx = document) {
    return Array.from(ctx.querySelectorAll(sel));
  }

  /* ============================================================
     CUSTOM CURSOR
     ============================================================ */
  function initCursor() {
    const dot  = $('#cursorDot');
    const ring = $('#cursorRing');

    if (!dot || !ring) return;

    // Hide on touch devices
    if (!window.matchMedia('(hover: hover)').matches) {
      dot.style.display  = 'none';
      ring.style.display = 'none';
      document.body.style.cursor = 'auto';
      return;
    }

    let mouseX = 0, mouseY = 0;
    let ringX  = 0, ringY  = 0;
    let rafId  = null;

    document.addEventListener('mousemove', (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      dot.style.left = mouseX + 'px';
      dot.style.top  = mouseY + 'px';
      if (!rafId) rafId = requestAnimationFrame(animateRing);
    });

    function animateRing() {
      rafId = null;
      ringX += (mouseX - ringX) * 0.18;
      ringY += (mouseY - ringY) * 0.18;
      ring.style.left = ringX + 'px';
      ring.style.top  = ringY + 'px';

      if (Math.abs(mouseX - ringX) > 0.1 || Math.abs(mouseY - ringY) > 0.1) {
        rafId = requestAnimationFrame(animateRing);
      }
    }

    // Hover state for interactive elements
    const interactiveSelectors = 'a, button, [role="button"], input, select, textarea, label, .product-card__image-wrap, .product-card__atc-btn';

    document.addEventListener('mouseover', (e) => {
      if (e.target.closest(interactiveSelectors)) {
        document.body.classList.add('cursor-hover');
      }
    });

    document.addEventListener('mouseout', (e) => {
      if (e.target.closest(interactiveSelectors)) {
        document.body.classList.remove('cursor-hover');
      }
    });

    // Disappear when leaving window
    document.addEventListener('mouseleave', () => {
      dot.style.opacity  = '0';
      ring.style.opacity = '0';
    });

    document.addEventListener('mouseenter', () => {
      dot.style.opacity  = '1';
      ring.style.opacity = '0.6';
    });
  }

  /* ============================================================
     STICKY HEADER
     ============================================================ */
  function initStickyHeader() {
    const header = $('#SiteHeader');
    if (!header) return;

    const scrollThreshold = 60;

    const onScroll = throttle(() => {
      const scrolled = window.scrollY > scrollThreshold;
      header.classList.toggle('is-scrolled', scrolled);
    }, 50);

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // run once on load
  }

  /* ============================================================
     SCROLL REVEAL — INTERSECTION OBSERVER
     ============================================================ */
  function initScrollReveal() {
    const revealClasses = ['.reveal', '.reveal-left', '.reveal-right', '.reveal-scale'];
    const elements      = $$(revealClasses.join(','));

    if (!elements.length) return;

    // Check if IntersectionObserver is available
    if (!('IntersectionObserver' in window)) {
      elements.forEach(el => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target); // animate only once
          }
        });
      },
      {
        threshold:  0.1,
        rootMargin: '0px 0px -40px 0px'
      }
    );

    elements.forEach(el => observer.observe(el));
  }

  /* ============================================================
     MOBILE DRAWER
     ============================================================ */
  function initMobileDrawer() {
    const toggle  = $('#MenuToggle');
    const drawer  = $('#MobileDrawer');
    const close   = $('#DrawerClose');
    const overlay = $('#DrawerOverlay');

    if (!toggle || !drawer) return;

    function openDrawer() {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      toggle.classList.add('is-active');
      document.body.style.overflow = 'hidden';

      // Focus first link for accessibility
      const firstLink = $('a, button', drawer);
      if (firstLink) setTimeout(() => firstLink.focus(), 400);
    }

    function closeDrawer() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.classList.remove('is-active');
      document.body.style.overflow = '';
      toggle.focus();
    }

    toggle.addEventListener('click', () => {
      const isOpen = drawer.classList.contains('is-open');
      isOpen ? closeDrawer() : openDrawer();
    });

    if (close) close.addEventListener('click', closeDrawer);
    if (overlay) overlay.addEventListener('click', closeDrawer);

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });

    // Focus trap
    drawer.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const focusableEls = $$('a, button, input, [tabindex="0"]', drawer).filter(el => !el.disabled);
      if (!focusableEls.length) return;

      const firstEl = focusableEls[0];
      const lastEl  = focusableEls[focusableEls.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    });
  }

  /* ============================================================
     SEARCH TOGGLE
     ============================================================ */
  function initSearchToggle() {
    const toggle    = $('#SearchToggle');
    const searchBar = $('#SearchBar');

    if (!toggle || !searchBar) return;

    let isOpen = false;

    toggle.addEventListener('click', () => {
      isOpen = !isOpen;
      searchBar.classList.toggle('is-open', isOpen);
      toggle.setAttribute('aria-expanded', String(isOpen));

      if (isOpen) {
        const input = $('input[type="search"]', searchBar);
        if (input) setTimeout(() => input.focus(), 300);
      }
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen) {
        isOpen = false;
        searchBar.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.focus();
      }
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (isOpen && !toggle.contains(e.target) && !searchBar.contains(e.target)) {
        isOpen = false;
        searchBar.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
      }
    });
  }

  /* ============================================================
     ADD TO CART — SHOPIFY CART API
     ============================================================ */
  function initAddToCart() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-atc-btn]');
      if (!btn) return;

      const variantId    = btn.dataset.variantId;
      const productTitle = btn.dataset.productTitle || 'Item';
      if (!variantId) return;

      // Loading state
      btn.disabled = true;
      btn.classList.add('is-loading');
      const originalHtml = btn.innerHTML;
      btn.innerHTML = '<span>ADDING...</span>';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            id:       parseInt(variantId, 10),
            quantity: 1,
          }),
        });

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.description || 'Could not add to cart');
        }

        const data = await response.json();

        // Update cart count
        updateCartCount();

        // Show notification
        showCartNotification(productTitle);

        // Success state
        btn.innerHTML = '<span>ADDED ✓</span>';
        setTimeout(() => {
          btn.innerHTML  = originalHtml;
          btn.disabled   = false;
          btn.classList.remove('is-loading');
        }, 2000);

      } catch (err) {
        console.error('[TC Cart]', err.message);
        btn.innerHTML = '<span>TRY AGAIN</span>';
        btn.disabled  = false;
        setTimeout(() => {
          btn.innerHTML = originalHtml;
          btn.classList.remove('is-loading');
        }, 2000);
      }
    });
  }

  /**
   * Fetch current cart item count and update the badge
   */
  async function updateCartCount() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      const badge = $('#CartCount');
      if (!badge) return;

      badge.textContent = cart.item_count;
      badge.classList.toggle('is-empty', cart.item_count === 0);

      // Bump animation
      badge.classList.remove('bump');
      void badge.offsetWidth; // reflow
      badge.classList.add('bump');
      badge.addEventListener('animationend', () => badge.classList.remove('bump'), { once: true });

    } catch (e) {
      // silent fail
    }
  }

  /**
   * Show cart notification toast
   * @param {string} title
   */
  function showCartNotification(title) {
    const notification = $('#cartNotification');
    const message      = $('#cartNotificationMessage');
    const closeBtn     = $('#cartNotificationClose');

    if (!notification) return;

    if (message) message.textContent = `"${title}" added to your cart.`;

    notification.classList.add('is-active');

    let timeout = setTimeout(hideCartNotification, 3000);

    if (closeBtn) {
      closeBtn.onclick = () => {
        clearTimeout(timeout);
        hideCartNotification();
      };
    }
  }

  function hideCartNotification() {
    const notification = $('#cartNotification');
    if (notification) notification.classList.remove('is-active');
  }

  /* ============================================================
     STAGGERED GRID ANIMATION
     Applies reveal-delay classes to product grid items
     ============================================================ */
  function initGridStagger() {
    $$('.featured-collection__grid').forEach(grid => {
      const items = $$('.featured-collection__grid-item', grid);
      items.forEach((item, i) => {
        const delay = ((i % 4) + 1);
        item.classList.add(`reveal-delay-${delay}`);
      });
    });
  }

  /* ============================================================
     SMOOTH SCROLL — anchor links with offset for header
     ============================================================ */
  function initSmoothScroll() {
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href^="#"]');
      if (!link) return;

      const id = link.getAttribute('href');
      if (id === '#') return;

      const target = $(id);
      if (!target) return;

      e.preventDefault();
      const headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 72;
      const y = target.getBoundingClientRect().top + window.scrollY - headerH - 16;

      window.scrollTo({ top: y, behavior: 'smooth' });
    });
  }

  /* ============================================================
     HERO PARALLAX (subtle)
     ============================================================ */
  function initHeroParallax() {
    const hero = $('#Hero');
    if (!hero) return;

    const img = $('.hero__bg-img', hero);
    if (!img) return;

    // Only on desktop where we can afford it
    if (window.matchMedia('(max-width: 768px)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = throttle(() => {
      const scrollY = window.scrollY;
      if (scrollY > window.innerHeight) return;
      img.style.transform = `scale(1.04) translateY(${scrollY * 0.25}px)`;
    }, 16);

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ============================================================
     HERO VIDEO — fade in on load, respect reduced motion
     ============================================================ */
  function initHeroVideo() {
    const video = document.querySelector('.hero__video');
    if (!video) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      video.removeAttribute('autoplay');
      video.pause();
      video.classList.add('is-loaded');
      return;
    }

    video.addEventListener('canplay', () => {
      video.classList.add('is-loaded');
    }, { once: true });

    // Fallback in case canplay fires late
    setTimeout(() => video.classList.add('is-loaded'), 1500);
  }

  /* ============================================================
     MARQUEE — pause on focus (accessibility)
     ============================================================ */
  function initMarqueePause() {
    $$('.marquee-banner__track').forEach(track => {
      track.addEventListener('focusin', () => {
        track.style.animationPlayState = 'paused';
      });
      track.addEventListener('focusout', () => {
        track.style.animationPlayState = 'running';
      });
    });
  }

  /* ============================================================
     PRODUCT CARD — Quantity input for PDP (if used)
     ============================================================ */
  function initQuantityButtons() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qty-btn]');
      if (!btn) return;

      const action = btn.dataset.qtyBtn;
      const input  = btn.closest('[data-qty-wrapper]')?.querySelector('input[name="quantity"]');
      if (!input) return;

      let val = parseInt(input.value, 10) || 1;

      if (action === 'increase') val = val + 1;
      if (action === 'decrease') val = Math.max(1, val - 1);

      input.value = val;
      input.dispatchEvent(new Event('change'));
    });
  }

  /* ============================================================
     PRODUCT GALLERY
     Color-filtered image gallery: filter by alt text, arrows, zoom
     Called before initProductConfigurator so window.TC.gallery
     is available when the configurator hooks in.
     ============================================================ */
  function initProductGallery() {
    const productJsonEl = $('#product-json');
    if (!productJsonEl) return;

    let product;
    try { product = JSON.parse(productJsonEl.textContent); }
    catch (e) { return; }

    if (!product || !Array.isArray(product.images) || !product.images.length) return;

    /* ── DOM REFS ────────────────────────────────────────────── */
    const mainImg       = $('#MainProductImage');
    if (!mainImg) return;

    const thumbsEl      = $('#ProductThumbs');
    const prevBtn       = $('#GalleryPrev');
    const nextBtn       = $('#GalleryNext');
    const countCurEl    = $('#ImgCountCurrent');
    const countTotEl    = $('#ImgCountTotal');
    const counterWrap   = document.querySelector('.product-main__img-counter');
    const zoomOverlay   = $('#GalleryZoom');
    const zoomImgEl     = $('#GalleryZoomImg');
    const zoomCloseBtn  = $('#GalleryZoomClose');
    const zoomPrevBtn   = $('#GalleryZoomPrev');
    const zoomNextBtn   = $('#GalleryZoomNext');
    const zoomCountCur  = $('#GalleryZoomCountCurrent');
    const zoomCountTot  = $('#GalleryZoomCountTotal');

    /* ── STATE ───────────────────────────────────────────────── */
    let currentImages = product.images.slice();
    let currentIndex  = 0;
    let isZoomOpen    = false;

    /* ── HELPERS ─────────────────────────────────────────────── */
    function shopifySrc(src, size) {
      if (!src) return src;
      if (src.startsWith('//')) src = 'https:' + src;
      return src.replace(/(\.[a-zA-Z]{3,4})(\?|$)/, '_' + size + '$1$2');
    }

    /* ── COLOR FILTER ────────────────────────────────────────── */
    // Returns filtered images when alt texts are set, or null when falling back.
    // Callers use null to know they should let the configurator handle the main image.
    function filterImagesByColor(colorName) {
      const color = (colorName || '').toLowerCase().trim();
      if (!color) return null;
      const filtered = product.images.filter(img => {
        const alt = (img.alt || '').toLowerCase().trim();
        return alt.startsWith(color);
      });
      return filtered.length > 0 ? filtered : null;
    }

    /* ── COUNTER ─────────────────────────────────────────────── */
    function updateCounter(idx, total) {
      if (counterWrap) counterWrap.style.display = total <= 1 ? 'none' : '';
      if (countCurEl) countCurEl.textContent = idx + 1;
      if (countTotEl) countTotEl.textContent = total;
      if (zoomCountCur) zoomCountCur.textContent = idx + 1;
      if (zoomCountTot) zoomCountTot.textContent = total;
    }

    /* ── SET MAIN IMAGE (crossfade) ──────────────────────────── */
    function setMainImage(idx) {
      if (!currentImages.length) return;
      idx = Math.max(0, Math.min(idx, currentImages.length - 1));
      currentIndex = idx;

      const imgData = currentImages[idx];
      const src = shopifySrc(String(imgData.src || imgData), '800x1000_crop_center');
      const alt = imgData.alt || product.title || '';

      // Crossfade
      mainImg.style.opacity   = '0';
      mainImg.style.transform = 'scale(1.03)';

      const preload = new Image();
      preload.src = src;
      const doSwap = () => {
        mainImg.src = src;
        mainImg.alt = alt;
        requestAnimationFrame(() => {
          mainImg.style.opacity   = '1';
          mainImg.style.transform = 'scale(1)';
        });
      };
      if (preload.complete) { doSwap(); } else { preload.onload = doSwap; }

      // Update active thumbnail highlight + scroll into view
      if (thumbsEl) {
        const thumbBtns = thumbsEl.querySelectorAll('.product-main__thumb');
        thumbBtns.forEach((t, i) => t.classList.toggle('is-active', i === idx));
        if (thumbBtns[idx]) {
          thumbBtns[idx].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        }
      }

      updateCounter(idx, currentImages.length);
      if (isZoomOpen) refreshZoomImage();
    }

    /* ── REBUILD THUMBNAILS ──────────────────────────────────── */
    function rebuildThumbs(images) {
      if (!thumbsEl) return;
      thumbsEl.innerHTML = '';

      images.forEach((img, i) => {
        const src    = shopifySrc(String(img.src || img), '120x150_crop_center');
        const alt    = img.alt || product.title || '';
        const btn    = document.createElement('button');
        btn.type     = 'button';
        btn.className = 'product-main__thumb' + (i === 0 ? ' is-active' : '');
        btn.dataset.thumb = String(i);
        btn.setAttribute('aria-label', `View image ${i + 1} of ${images.length}`);

        const imgEl    = document.createElement('img');
        imgEl.src      = src;
        imgEl.alt      = alt;
        imgEl.width    = 120;
        imgEl.height   = 150;
        imgEl.loading  = 'lazy';

        btn.appendChild(imgEl);
        thumbsEl.appendChild(btn);
      });
    }

    /* ── PUBLIC: FILTER BY COLOR ─────────────────────────────── */
    // Returns true when color-specific images were found (alt texts set),
    // false when falling back — configurator uses this to decide whether to
    // switch the main image via variant.featured_image instead.
    function filterByColor(colorName) {
      const filtered = filterImagesByColor(colorName);
      const hasColorImages = filtered !== null;

      currentImages = filtered || product.images.slice();
      currentIndex  = 0;
      rebuildThumbs(currentImages);

      // Only auto-switch main image when we found color-tagged images.
      // When falling back (no alt texts set), the configurator's
      // variant.featured_image will drive the main image instead.
      if (hasColorImages) setMainImage(0);

      const showArrows = currentImages.length > 1;
      if (prevBtn) prevBtn.hidden = !showArrows;
      if (nextBtn) nextBtn.hidden = !showArrows;

      return hasColorImages;
    }

    /* ── ARROW NAVIGATION ────────────────────────────────────── */
    function goPrev() {
      const idx = currentIndex === 0 ? currentImages.length - 1 : currentIndex - 1;
      setMainImage(idx);
    }
    function goNext() {
      const idx = currentIndex === currentImages.length - 1 ? 0 : currentIndex + 1;
      setMainImage(idx);
    }

    if (prevBtn) prevBtn.addEventListener('click', goPrev);
    if (nextBtn) nextBtn.addEventListener('click', goNext);

    /* ── THUMBNAIL CLICK (event delegation) ──────────────────── */
    // Works on both Liquid-rendered and JS-rebuilt thumbnails
    if (thumbsEl) {
      thumbsEl.addEventListener('click', (e) => {
        const btn = e.target.closest('.product-main__thumb');
        if (!btn) return;
        const idx = parseInt(btn.dataset.thumb, 10);
        if (!isNaN(idx)) setMainImage(idx);
      });
    }

    /* ── ZOOM OVERLAY ────────────────────────────────────────── */
    function refreshZoomImage() {
      if (!zoomImgEl || !currentImages[currentIndex]) return;
      const imgData = currentImages[currentIndex];
      const src = shopifySrc(String(imgData.src || imgData), '1200x1500_crop_center');
      zoomImgEl.style.opacity = '0';
      zoomImgEl.onload = () => { zoomImgEl.style.opacity = '1'; };
      zoomImgEl.src = src;
      zoomImgEl.alt = imgData.alt || product.title || '';
      if (zoomCountCur) zoomCountCur.textContent = currentIndex + 1;
      if (zoomCountTot) zoomCountTot.textContent = currentImages.length;
    }

    function openZoom() {
      if (!zoomOverlay) return;
      isZoomOpen = true;
      zoomOverlay.classList.add('is-open');
      zoomOverlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      refreshZoomImage();
      if (zoomCloseBtn) setTimeout(() => zoomCloseBtn.focus(), 60);
    }

    function closeZoom() {
      if (!zoomOverlay) return;
      isZoomOpen = false;
      zoomOverlay.classList.remove('is-open');
      zoomOverlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      mainImg.focus();
    }

    mainImg.addEventListener('click', openZoom);

    if (zoomCloseBtn) zoomCloseBtn.addEventListener('click', closeZoom);

    if (zoomOverlay) {
      zoomOverlay.addEventListener('click', (e) => {
        if (e.target === zoomOverlay) closeZoom();
      });
    }

    if (zoomPrevBtn) zoomPrevBtn.addEventListener('click', () => {
      const idx = currentIndex === 0 ? currentImages.length - 1 : currentIndex - 1;
      setMainImage(idx);
    });

    if (zoomNextBtn) zoomNextBtn.addEventListener('click', () => {
      const idx = currentIndex === currentImages.length - 1 ? 0 : currentIndex + 1;
      setMainImage(idx);
    });

    // Keyboard navigation (ESC to close, arrows to navigate)
    document.addEventListener('keydown', (e) => {
      if (!isZoomOpen) return;
      if (e.key === 'Escape') { closeZoom(); return; }
      if (e.key === 'ArrowLeft')  {
        const idx = currentIndex === 0 ? currentImages.length - 1 : currentIndex - 1;
        setMainImage(idx);
      }
      if (e.key === 'ArrowRight') {
        const idx = currentIndex === currentImages.length - 1 ? 0 : currentIndex + 1;
        setMainImage(idx);
      }
    });

    /* ── INIT ARROWS ─────────────────────────────────────────── */
    const initShowArrows = product.images.length > 1;
    if (prevBtn) prevBtn.hidden = !initShowArrows;
    if (nextBtn) nextBtn.hidden = !initShowArrows;

    // Initial counter
    updateCounter(0, product.images.length);

    /* ── EXPOSE FOR CONFIGURATOR ─────────────────────────────── */
    window.TC = window.TC || {};
    window.TC.gallery = {
      filterByColor,
      setMainImage,
      get currentImages() { return currentImages; },
      get currentIndex()  { return currentIndex; },
    };
  }

  /* ============================================================
     PRODUCT ACCORDION
     Handles accordion toggles on the product detail page
     ============================================================ */
  function initProductAccordion() {
    $$('.product-main__accordion-trigger').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const item   = trigger.closest('.product-main__accordion-item');
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';

        // Close all
        $$('.product-main__accordion-item').forEach(i => {
          i.querySelector('.product-main__accordion-trigger')
            ?.setAttribute('aria-expanded', 'false');
          i.classList.remove('is-open');
          const body = i.querySelector('.product-main__accordion-body');
          if (body) body.hidden = true;
        });

        // Toggle clicked
        if (!isOpen) {
          trigger.setAttribute('aria-expanded', 'true');
          item?.classList.add('is-open');
          const body = item?.querySelector('.product-main__accordion-body');
          if (body) body.hidden = false;
        }
      });
    });
  }

  /* ============================================================
     PRODUCT CONFIGURATOR
     Live 2D variant selector: color + size → exact variant match
     URL sync, dynamic availability, ATC with feedback
     ============================================================ */
  function initProductConfigurator() {
    const productJsonEl = $('#product-json');
    if (!productJsonEl) return;

    const metaEl = $('#product-cfg-meta');

    let product, meta;
    try {
      product = JSON.parse(productJsonEl.textContent);
      meta    = metaEl ? JSON.parse(metaEl.textContent) : {};
    } catch (e) { return; }

    if (!product || !Array.isArray(product.variants)) return;

    /* ── COLOR MAP ───────────────────────────────────────────── */
    const COLOR_MAP = {
      'black': '#000000', 'white': '#ffffff', 'off-white': '#F0EDE6',
      'dirty-white': '#F0EDE6', 'pink': '#FF1F6E', 'hot-pink': '#FF1F6E',
      'red': '#e63946', 'blue': '#1d3557', 'navy': '#0a1628',
      'navy-blue': '#0a1628', 'grey': '#888888', 'gray': '#888888',
      'charcoal': '#3a3a3a', 'brown': '#6b4c3b', 'tan': '#c4a882',
      'green': '#2d6a4f', 'olive': '#6b6b2a', 'yellow': '#ffd60a',
      'gold': '#c9a84c', 'purple': '#7b2d8b', 'orange': '#f4742b',
      'silver': '#C0C0C0', 'chrome': '#C0C0C0', 'camouflage': '#78866b',
      'camo': '#78866b',
    };

    /* ── OPTION INDICES ──────────────────────────────────────── */
    const colorIdx = (meta.colorOptionIndex !== undefined) ? meta.colorOptionIndex : -1;
    const sizeIdx  = (meta.sizeOptionIndex  !== undefined) ? meta.sizeOptionIndex  : -1;

    /* ── STATE ───────────────────────────────────────────────── */
    let selectedColor     = null;
    let selectedSize      = null;
    let selectedVariantId = meta.currentVariantId || null;
    let qty               = 1;

    /* ── DOM REFS ────────────────────────────────────────────── */
    const swatches      = $$('[data-cfg-swatch]');
    const sizeBtns      = $$('[data-cfg-size]');
    const qtyMinus      = $('#CfgQtyMinus');
    const qtyPlus       = $('#CfgQtyPlus');
    const qtyInput      = $('#CfgQtyInput');
    const atcBtn        = $('#ProductATC');
    const priceEl       = $('#ProductPrice');
    const compareEl     = $('#ProductComparePrice');
    const savingsEl     = $('#ProductSavings');
    const mainImg       = $('#MainProductImage');
    const previewColor  = $('#CfgPreviewColor');
    const previewSize   = $('#CfgPreviewSize');
    const colorNameEl   = $('#SelectedColorName');
    const sizeNameEl    = $('#SelectedSizeName');
    const statusEl      = $('#CfgStatus');
    const thumbs        = $$('[data-thumb]');
    const imgCountEl    = $('#ImgCountCurrent');

    /* ── MONEY FORMAT ────────────────────────────────────────── */
    function formatMoney(cents) {
      const amountKey = '{' + '{amount}}';
      const noDecKey  = '{' + '{amount_no_decimals}}';
      const fmt = (window.TC && window.TC.moneyFormat) || meta.moneyFormat || ('$' + amountKey);
      const dollars = (cents / 100).toFixed(2);
      return fmt
        .replace(amountKey, dollars)
        .replace(noDecKey,  String(Math.floor(cents / 100)));
    }

    /* ── IMAGE URL SIZING ────────────────────────────────────── */
    // Shopify CDN URL manipulation for sized images
    function shopifyImageSrc(src, size) {
      if (!src) return src;
      if (src.startsWith('//')) src = 'https:' + src;
      return src.replace(/(\.[a-zA-Z]{3,4})(\?|$)/, '_' + size + '$1$2');
    }

    /* ── VARIANT MATCHING (2D) ───────────────────────────────── */
    // Find variant matching BOTH selected color and size
    function findVariant(color, size) {
      return product.variants.find(v => {
        const colorOk = colorIdx < 0 || color === null || v.options[colorIdx] === color;
        const sizeOk  = sizeIdx  < 0 || size  === null || v.options[sizeIdx]  === size;
        return colorOk && sizeOk;
      }) || null;
    }

    /* ── PRICE UPDATE ────────────────────────────────────────── */
    function updatePrice(variant) {
      if (!variant || !priceEl) return;
      const price   = variant.price;
      const compare = variant.compare_at_price || 0;
      const isOnSale = compare > 0 && compare > price;

      priceEl.textContent = formatMoney(price);
      priceEl.classList.toggle('product-main__price--sale', isOnSale);
      priceEl.setAttribute('content', (price / 100).toFixed(2));

      if (compareEl) {
        compareEl.textContent = isOnSale ? formatMoney(compare) : '';
        compareEl.style.display = isOnSale ? '' : 'none';
      }
      if (savingsEl) {
        if (isOnSale) {
          const pct = Math.round((compare - price) / compare * 100);
          savingsEl.textContent = 'SAVE ' + pct + '%';
          savingsEl.style.display = '';
        } else {
          savingsEl.style.display = 'none';
        }
      }
    }

    /* ── IMAGE CROSSFADE ─────────────────────────────────────── */
    function switchImage(src, alt) {
      if (!mainImg || !src) return;
      mainImg.style.opacity   = '0';
      mainImg.style.transform = 'scale(1.03)';
      const img = new Image();
      img.src = src;
      const doSwap = () => {
        mainImg.src = src;
        if (alt !== undefined) mainImg.alt = alt;
        requestAnimationFrame(() => {
          mainImg.style.opacity   = '1';
          mainImg.style.transform = 'scale(1)';
        });
      };
      if (img.complete) doSwap(); else img.onload = doSwap;
    }

    /* ── ATC BUTTON STATE ────────────────────────────────────── */
    function updateATCState(variant) {
      if (!atcBtn) return;
      const textEl = atcBtn.querySelector('.product-main__atc-text');

      if (!variant) {
        atcBtn.disabled = true;
        atcBtn.classList.add('product-main__atc--disabled');
        if (textEl) textEl.textContent = 'UNAVAILABLE';
        if (statusEl) {
          statusEl.textContent = 'This combination is not available.';
          statusEl.className = 'product-cfg__status product-cfg__status--unavailable';
        }
      } else if (!variant.available) {
        atcBtn.disabled = true;
        atcBtn.classList.add('product-main__atc--disabled');
        if (textEl) textEl.textContent = 'SOLD OUT';
        if (statusEl) {
          statusEl.textContent = 'This option is currently sold out.';
          statusEl.className = 'product-cfg__status product-cfg__status--unavailable';
        }
      } else {
        atcBtn.disabled = false;
        atcBtn.classList.remove('product-main__atc--disabled');
        atcBtn.dataset.variantId = String(variant.id);
        if (textEl) textEl.textContent = 'ADD TO CART';
        if (statusEl) {
          statusEl.textContent = '';
          statusEl.className = 'product-cfg__status';
        }
      }
    }

    /* ── URL SYNC ────────────────────────────────────────────── */
    function updateURL(variantId) {
      if (!variantId || !history.replaceState) return;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variantId));
        history.replaceState({}, '', url.toString());
      } catch (e) {}
    }

    /* ── SIZE AVAILABILITY FOR SELECTED COLOR ────────────────── */
    // When color changes, update which sizes are available
    function updateSizeAvailability(color) {
      sizeBtns.forEach(btn => {
        const size    = btn.dataset.size;
        const variant = findVariant(color, size);
        const avail   = variant ? variant.available : false;
        btn.classList.toggle('is-sold-out', !avail);
        btn.setAttribute('aria-disabled', avail ? 'false' : 'true');
      });
    }

    /* ── FULL STATE → UI UPDATE ──────────────────────────────── */
    function updateFromSelection() {
      const variant = findVariant(selectedColor, selectedSize);
      selectedVariantId = variant ? variant.id : null;

      updatePrice(variant);
      updateATCState(variant);
      if (variant) updateURL(variant.id);

      // Switch main image to the variant's featured image.
      // This is the reliable fallback that works even without alt-text-tagged images.
      // When color-tagged images ARE found, the gallery's filterByColor() calls
      // setMainImage(0) first, then this runs with (likely) the same image — no flicker.
      if (variant && colorIdx >= 0 && variant.featured_image) {
        const fi  = variant.featured_image;
        const src = fi.src || fi;
        if (src) {
          const sized = shopifyImageSrc(String(src), '800x1000_crop_center');
          const alt   = fi.alt || '';
          switchImage(sized, alt);
        }
      }

      // Update preview labels
      if (previewColor) previewColor.textContent = selectedColor || '';
      if (previewSize)  previewSize.textContent  = selectedSize  || '';
    }

    /* ── SET ACTIVE COLOR UI ─────────────────────────────────── */
    function setActiveColor(color) {
      swatches.forEach(s => {
        const active = s.dataset.color === color;
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      if (colorNameEl) colorNameEl.textContent = color || '';
    }

    /* ── SET ACTIVE SIZE UI ──────────────────────────────────── */
    function setActiveSize(size) {
      sizeBtns.forEach(b => {
        const active = b.dataset.size === size;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      if (sizeNameEl) sizeNameEl.textContent = size || '';
    }

    /* ── APPLY COLOR MAP TO SWATCHES ─────────────────────────── */
    function applySwatchColors() {
      swatches.forEach(swatch => {
        const color  = (swatch.dataset.color || '').toLowerCase().trim();
        const handle = color.replace(/\s+/g, '-');
        const hex    = COLOR_MAP[handle] || COLOR_MAP[color];
        if (hex) swatch.style.backgroundColor = hex;
      });
    }

    /* ── INIT FROM URL ?variant= ─────────────────────────────── */
    function initFromURL() {
      try {
        const params    = new URLSearchParams(window.location.search);
        const variantId = parseInt(params.get('variant') || '0', 10);
        if (!variantId) return false;
        const variant = product.variants.find(v => v.id === variantId);
        if (!variant) return false;
        if (colorIdx >= 0) selectedColor = variant.options[colorIdx] || null;
        if (sizeIdx  >= 0) selectedSize  = variant.options[sizeIdx]  || null;
        return true;
      } catch (e) { return false; }
    }

    /* ── EVENT LISTENERS ─────────────────────────────────────── */

    // Color swatch click
    swatches.forEach(swatch => {
      swatch.addEventListener('click', () => {
        if (swatch.classList.contains('is-sold-out')) return;
        selectedColor = swatch.dataset.color || null;
        setActiveColor(selectedColor);

        // Filter gallery images by selected color
        if (window.TC && window.TC.gallery) {
          window.TC.gallery.filterByColor(selectedColor);
        }

        // Re-evaluate size availability for new color
        if (sizeIdx >= 0) {
          updateSizeAvailability(selectedColor);
          // If selected size is now unavailable, keep it selected but let
          // updateATCState show sold-out — don't auto-deselect (better UX)
        }

        updateFromSelection();
      });
    });

    // Size button click
    sizeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('is-sold-out')) return;
        selectedSize = btn.dataset.size || null;
        setActiveSize(selectedSize);
        updateFromSelection();
      });
    });

    // Quantity controls
    function setQty(val) {
      qty = Math.max(1, Math.min(10, parseInt(val, 10) || 1));
      if (qtyInput) qtyInput.value = qty;
    }
    if (qtyMinus) qtyMinus.addEventListener('click', () => setQty(qty - 1));
    if (qtyPlus)  qtyPlus.addEventListener('click',  () => setQty(qty + 1));
    if (qtyInput) {
      qtyInput.addEventListener('change', () => setQty(qtyInput.value));
      qtyInput.addEventListener('blur',   () => setQty(qtyInput.value));
    }

    // Thumbnail click — only used as fallback when gallery is not active.
    // initProductGallery installs a delegated handler on #ProductThumbs that
    // supersedes these once it runs.
    if (!window.TC || !window.TC.gallery) {
      thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => {
          const idx = parseInt(thumb.dataset.thumb, 10);
          switchImage(thumb.dataset.src, thumb.dataset.alt);
          thumbs.forEach(t => t.classList.toggle('is-active', parseInt(t.dataset.thumb, 10) === idx));
          if (imgCountEl) imgCountEl.textContent = idx + 1;
        });
      });
    }

    // Add to Cart
    if (atcBtn) {
      atcBtn.addEventListener('click', async () => {
        if (atcBtn.disabled) return;
        const varId = parseInt(atcBtn.dataset.variantId, 10);
        if (!varId) return;

        const textEl   = atcBtn.querySelector('.product-main__atc-text');
        const origText = textEl ? textEl.textContent : 'ADD TO CART';

        atcBtn.classList.add('is-loading');
        atcBtn.disabled = true;

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: varId, quantity: qty }),
          });

          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.description || 'Could not add to cart');
          }

          // Update cart count badge
          if (window.TC && window.TC.updateCartCount) {
            window.TC.updateCartCount();
          }

          // Cart toast notification
          showCartNotification(product.title || 'Item');

          // Success state
          atcBtn.classList.remove('is-loading');
          atcBtn.classList.add('is-added');
          if (textEl) textEl.textContent = '✓ ADDED TO CART';

          setTimeout(() => {
            atcBtn.classList.remove('is-added');
            atcBtn.disabled = false;
            if (textEl) textEl.textContent = origText;
          }, 2500);

        } catch (err) {
          atcBtn.classList.remove('is-loading');
          atcBtn.disabled = false;
          if (textEl) {
            textEl.textContent = 'TRY AGAIN';
            setTimeout(() => { if (textEl) textEl.textContent = origText; }, 2000);
          }
          if (statusEl) {
            statusEl.textContent = err.message || 'Something went wrong.';
            statusEl.className = 'product-cfg__status product-cfg__status--error';
            setTimeout(() => {
              statusEl.textContent = '';
              statusEl.className = 'product-cfg__status';
            }, 3000);
          }
          console.warn('[TC Configurator] Cart error:', err.message);
        }
      });
    }

    /* ── INITIALIZE ──────────────────────────────────────────── */
    applySwatchColors();

    // Pre-select from URL ?variant= or fall back to Liquid current variant
    const fromURL = initFromURL();

    if (!fromURL) {
      const currentV = product.variants.find(v => v.id === meta.currentVariantId);
      if (currentV) {
        if (colorIdx >= 0) selectedColor = currentV.options[colorIdx] || null;
        if (sizeIdx  >= 0) selectedSize  = currentV.options[sizeIdx]  || null;
      }
    }

    // Apply initial UI
    if (selectedColor) setActiveColor(selectedColor);
    if (selectedSize)  {
      if (selectedColor && sizeIdx >= 0) updateSizeAvailability(selectedColor);
      setActiveSize(selectedSize);
    }

    // Set preview labels
    if (previewColor) previewColor.textContent = selectedColor || '';
    if (previewSize)  previewSize.textContent  = selectedSize  || '';

    // Run initial variant state (price, ATC, URL)
    updateFromSelection();

    // Sync gallery to the pre-selected color (from URL or Liquid default)
    if (selectedColor && window.TC && window.TC.gallery) {
      window.TC.gallery.filterByColor(selectedColor);
    }
  }

  /* ============================================================
     CART PAGE
     ============================================================ */

  /**
   * POST to /cart/change.js and return updated cart object
   * @param {string} key   — line item key
   * @param {number} qty   — new quantity (0 = remove)
   * @returns {Promise<object>}
   */
  async function updateCartItem(key, qty) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
    if (!res.ok) throw new Error('Cart update failed');
    return res.json();
  }

  /**
   * Refresh the subtotal, total, and free-shipping bar
   * from the latest cart object without a full page reload
   * @param {object} cart — response from /cart/change.js
   */
  function updateCartTotals(cart) {
    const subtotalEl = $('#CartSubtotal');
    const totalEl    = $('#CartTotal');
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    if (totalEl)    totalEl.textContent    = formatMoney(cart.total_price);

    // Free shipping bar (threshold = $75 = 7500 cents)
    const threshold  = 7500;
    const remaining  = threshold - cart.total_price;
    const pct        = Math.min(100, Math.round((cart.total_price / threshold) * 100));

    const fillEl  = $('#CartShippingFill');
    const textEl  = $('#CartShippingText');
    if (fillEl) fillEl.style.width = pct + '%';
    if (textEl) {
      textEl.textContent = remaining > 0
        ? `You're ${formatMoney(remaining)} away from free shipping!`
        : "You've unlocked free shipping! 🎉";
    }
  }

  /**
   * Format cents to a money string matching the store's currency symbol.
   * Falls back to a simple $ format if Shopify's moneyFormat is unavailable.
   * @param {number} cents
   * @returns {string}
   */
  function formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    // Use Shopify's currency symbol if available via meta tag
    const symbol = document.querySelector('meta[name="currency-symbol"]');
    return (symbol ? symbol.content : '$') + dollars;
  }

  /**
   * Initialise cart page interactions: quantity steppers and remove buttons.
   * Only runs when #CartItemsList is present (i.e. on the cart page).
   */
  function initCartPage() {
    const list = $('#CartItemsList');
    if (!list) return;

    // Debounced quantity change handler
    const debouncedUpdate = debounce(async function (key, input) {
      const newQty = parseInt(input.value, 10);
      if (isNaN(newQty) || newQty < 0) return;
      try {
        const cart = await updateCartItem(key, newQty);
        updateCartCount();
        updateCartTotals(cart);

        // Update line total for this item
        const lineTotalEl = $(`[data-line-total="${key}"]`);
        if (lineTotalEl) {
          const lineItem = cart.items.find(i => i.key === key);
          if (lineItem) lineTotalEl.textContent = formatMoney(lineItem.final_line_price);
        }

        // If quantity became 0, fade out and remove the row
        if (newQty === 0) {
          removeItemRow(key, cart);
        }
      } catch (e) {
        // silent fail — input stays as-is
      }
    }, 500);

    // Delegate clicks on + / − buttons and changes on quantity inputs
    list.addEventListener('click', function (e) {
      const btn = e.target.closest('.cart-item__qty-btn');
      if (!btn) return;

      const container = btn.closest('[data-key]');
      const key       = container.dataset.key;
      const input     = container.querySelector('.cart-item__qty-input');
      if (!input) return;

      let val = parseInt(input.value, 10) || 1;
      if (btn.dataset.action === 'increase') val = Math.min(val + 1, 99);
      if (btn.dataset.action === 'decrease') val = Math.max(val - 1, 0);
      input.value = val;
      debouncedUpdate(key, input);
    });

    list.addEventListener('change', function (e) {
      const input = e.target.closest('.cart-item__qty-input');
      if (!input) return;
      const key = input.dataset.key;
      debouncedUpdate(key, input);
    });

    // Remove buttons
    list.addEventListener('click', function (e) {
      const btn = e.target.closest('.cart-item__remove');
      if (!btn) return;
      const key = btn.dataset.key;
      removeItem(key);
    });
  }

  /**
   * Remove an item from the cart, animate its row out, then clean up DOM
   * @param {string} key
   */
  async function removeItem(key) {
    try {
      const cart = await updateCartItem(key, 0);
      updateCartCount();
      updateCartTotals(cart);
      removeItemRow(key, cart);
    } catch (e) {
      // silent fail
    }
  }

  /**
   * Fade out a cart item row, then remove it from the DOM.
   * If the cart is now empty, reload the page to show the empty state.
   * @param {string} key
   * @param {object} cart
   */
  function removeItemRow(key, cart) {
    const safeId = key.replace(/:/g, '-');
    const row    = $(`#CartItem-${safeId}`);
    if (!row) return;

    row.classList.add('is-removing');
    row.addEventListener('transitionend', function handler() {
      row.removeEventListener('transitionend', handler);
      row.remove();

      // If no items remain, reload to show the empty cart state
      if (cart.item_count === 0) {
        window.location.reload();
      }
    });
  }

  /* ============================================================
     INIT — run everything on DOMContentLoaded
     ============================================================ */
  function init() {
    initCursor();
    initStickyHeader();
    initScrollReveal();
    initMobileDrawer();
    initSearchToggle();
    initAddToCart();
    initGridStagger();
    initSmoothScroll();
    initHeroParallax();
    initHeroVideo();
    initMarqueePause();
    initQuantityButtons();
    initProductAccordion();
    initProductGallery();      // must run before configurator so gallery is ready
    initProductConfigurator();
    initCartPage();

    // Remove page overlay after load
    const overlay = document.querySelector('.page-overlay');
    if (overlay) {
      overlay.addEventListener('animationend', () => overlay.remove(), { once: true });
    }

    // Expose cart update globally for other scripts
    window.TC = window.TC || {};
    window.TC.updateCartCount = updateCartCount;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

document.getElementById('size-guide-modal')
  ?.addEventListener('click', function(e) {
    if (e.target === this) {
      this.style.display = 'none';
    }
  });

// Promo Popup
function showPromoPopup() {
  if (!sessionStorage.getItem('promoSeen')) {
    setTimeout(function() {
      const popup = document.getElementById('promo-popup');
      if (popup) popup.classList.add('active');
    }, 2000);
  }
}

function closePromoPopup() {
  const popup = document.getElementById('promo-popup');
  if (popup) popup.classList.remove('active');
  sessionStorage.setItem('promoSeen', 'true');
}

function copyCode() {
  navigator.clipboard.writeText('CHICK23').then(function() {
    const btn = document.querySelector('.promo-popup__copy');
    if (!btn) return;
    btn.textContent = 'COPIED ✓';
    btn.style.background = '#2d6a4f';
    setTimeout(function() {
      btn.textContent = 'COPY';
      btn.style.background = '';
    }, 2000);
  });
}

document.addEventListener('DOMContentLoaded', showPromoPopup);
